import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';

const partnersData = {
  arinco: {
    id: 'arinco',
    logo: '/assets/partners/arinco-logo.png',
    categories: ['unterweisungssystem', 'arbeitssicherheit'],
    website: 'https://www.arinco.at',
    title: {
      de: 'arinco – Kompetenzpartner für Arbeitssicherheit',
      en: 'arinco – Competence Partner for Workplace Safety'
    },
    content: {
      de: {
        intro: 'Das Sicherheitstechnische Zentrum arinco unterstützt Unternehmen und Gesundheitseinrichtungen in allen Fragen der betrieblichen Arbeitssicherheit und Prävention.',
        partnership: 'Als inhaltlicher Partner bringt arinco seine fachliche Expertise in die Entwicklung und Aktualisierung unserer e-Training Module für Arbeitssicherheit ein.',
        value: 'Durch diese Zusammenarbeit stellen wir sicher, dass unsere digitalen Schulungen praxisnah, fachlich fundiert und gesetzeskonform sind.',
        focusTitle: 'Gemeinsame Schwerpunkte',
        focusPoints: [
          'Arbeitssicherheit und Prävention',
          'gesetzeskonforme Unterweisungen',
          'fachliche Qualitätssicherung der Inhalte'
        ],
        visitPartner: 'Besuchen Sie die Seite unseres Partners',
        solutionsTitle: 'Eingesetzte Lösungen und Produkte'
      },
      en: {
        intro: 'The Safety Technology Center arinco supports companies and healthcare facilities in all matters of occupational safety and prevention.',
        partnership: 'As a content partner, arinco contributes its professional expertise to the development and updating of our e-training modules for workplace safety.',
        value: 'Through this collaboration, we ensure that our digital training courses are practical, professionally sound and legally compliant.',
        focusTitle: 'Joint Focus Areas',
        focusPoints: [
          'Workplace safety and prevention',
          'Legally compliant instructions',
          'Professional quality assurance of content'
        ],
        visitPartner: 'Visit our partner\'s website',
        solutionsTitle: 'Solutions and Products Used'
      }
    },
    links: [
      { to: '/instruction-system', label: { de: 'Unterweisungssystem', en: 'Instruction System' } },
      { to: '/produktkatalog', label: { de: 'Produktkatalog', en: 'Product Catalog' } }
    ]
  },
  acp: {
    id: 'acp',
    logo: '/assets/partners/acp-logo.png',
    categories: ['unterweisungssystem', 'awareness-trainings'],
    website: 'https://www.acp-gruppe.com/de-at/elearning',
    title: {
      de: 'Schulungen für Mitarbeitende zu den Themen IT-Security und KI-Awareness',
      en: 'Training for Employees on IT-Security and AI-Awareness'
    },
    content: {
      de: {
        intro: 'ACP unterstützt Unternehmen in Österreich mit ganzheitlichen IT Lösungen, individueller Beratung und praxisnahen Awareness Trainings.',
        partnership: 'ACP und e-Matrix entwickeln gemeinsam moderne Awareness Trainings, die Unternehmen in Österreich dabei unterstützen, ihre Mitarbeitenden in den Bereichen DSGVO, Cybersecurity und verantwortungsvollem Umgang mit künstlicher Intelligenz zu stärken. Die Trainings kombinieren realistische Szenarien und leicht verständliche Fachinformationen und bieten damit ein wirksames Lernangebot für Organisationen jeder Größe in der gesamten DACH Region.',
        trainingsTitle: 'Unsere gemeinsamen Trainings',
        trainings: [
          {
            name: 'Fit4 AI Compact',
            description: 'vermittelt Mitarbeitenden die Grundlagen eines verantwortungsvollen Umgangs mit künstlicher Intelligenz nach den gesetzlichen Vorgaben des EU AI Acts und zeigt, wie KI sicher und regelkonform im Arbeitsalltag genutzt werden kann.'
          },
          {
            name: 'Fit4 DSGVO',
            description: 'vermittelt die wichtigsten Anforderungen der Datenschutzgrundverordnung in klarer und verständlicher Form und unterstützt Mitarbeitende dabei, personenbezogene Daten sicher und gesetzeskonform zu verarbeiten.'
          },
          {
            name: 'Fit4Cyber',
            description: 'sensibilisiert für aktuelle Cyberbedrohungen und vermittelt konkrete Handlungsprinzipien zur Minimierung digitaler Risiken im österreichischen Arbeitsalltag und darüber hinaus.'
          }
        ],
        visitPartner: 'Besuchen Sie die Seite unseres Partners',
        solutionsTitle: 'Eingesetzte Lösungen und Produkte'
      },
      en: {
        intro: 'ACP supports companies in Austria with comprehensive IT solutions, individual consulting and practical awareness training.',
        partnership: 'ACP and e-Matrix jointly develop modern awareness training that supports companies in Austria in strengthening their employees in the areas of GDPR, cybersecurity and responsible use of artificial intelligence. The training combines realistic scenarios and easy-to-understand specialist information, providing an effective learning offer for organizations of all sizes throughout the DACH region.',
        trainingsTitle: 'Our Joint Training Programs',
        trainings: [
          {
            name: 'Fit4 AI Compact',
            description: 'teaches employees the basics of responsible use of artificial intelligence in accordance with the legal requirements of the EU AI Act and shows how AI can be used safely and in compliance with regulations in everyday work.'
          },
          {
            name: 'Fit4 GDPR',
            description: 'conveys the most important requirements of the General Data Protection Regulation in a clear and understandable form and supports employees in processing personal data securely and in compliance with the law.'
          },
          {
            name: 'Fit4Cyber',
            description: 'raises awareness of current cyber threats and teaches concrete principles of action to minimize digital risks in the Austrian workplace and beyond.'
          }
        ],
        visitPartner: 'Visit our partner\'s website',
        solutionsTitle: 'Solutions and Products Used'
      }
    },
    links: [
      { to: '/instruction-system', label: { de: 'Unterweisungssystem', en: 'Instruction System' } },
      { to: '/produktkatalog?category=cyber-security', label: { de: 'Produktkatalog – Cyber Security', en: 'Product Catalog – Cyber Security' } }
    ]
  },
  'wifi-ooe': {
    id: 'wifi-ooe',
    logo: '/assets/partners/wifi-logo.jpg',
    categories: ['arbeitssicherheit'],
    website: 'https://www.wifi-ooe.at/k/arbeitnehmerschutz-sicherheitstechnik',
    title: {
      de: 'WIFI Oberösterreich – Partner in der Ausbildung von Sicherheitsfachkräften',
      en: 'WIFI Upper Austria – Partner in Safety Officer Training'
    },
    content: {
      de: {
        intro: 'Das WIFI Oberösterreich zählt zu den führenden Ausbildungsinstitutionen im Bereich Arbeitssicherheit und betriebliche Prävention.',
        partnership: 'Im Rahmen der Ausbildung von Sicherheitsfachkräften (SFK) und Sicherheitsvertrauenspersonen (SVP) arbeiten das WIFI Oberösterreich und e-Matrix zusammen. Dabei werden die Health & Safety Arbeitssicherheits-e-Trainings von e-Matrix in der Ausbildung eingesetzt.',
        value: 'Die Zusammenarbeit verbindet die langjährige Ausbildungskompetenz des WIFI Oberösterreich mit den digitalen Schulungslösungen von e-Matrix und zeigt, wie moderne Schulungsformen im Bereich Arbeitssicherheit effizient umgesetzt werden können.',
        impact: 'Viele Sicherheitsfachkräfte lernen dadurch bereits während ihrer Ausbildung digitale Unterweisungs- und e-Training-Lösungen von e-Matrix kennen.',
        visitPartner: 'Besuchen Sie die Seite unseres Partners',
        solutionsTitle: 'Eingesetzte Lösungen und Produkte'
      },
      en: {
        intro: 'WIFI Upper Austria is one of the leading training institutions in the field of occupational safety and operational prevention.',
        partnership: 'WIFI Upper Austria and e-Matrix work together in the training of safety specialists (SFK) and safety representatives (SVP). The Health & Safety workplace safety e-training courses from e-Matrix are used in the training.',
        value: 'The collaboration combines WIFI Upper Austria\'s many years of training expertise with e-Matrix\'s digital training solutions and shows how modern training formats in the area of workplace safety can be implemented efficiently.',
        impact: 'Many safety specialists become familiar with e-Matrix\'s digital instruction and e-training solutions during their training.',
        visitPartner: 'Visit our partner\'s website',
        solutionsTitle: 'Solutions and Products Used'
      }
    },
    links: [
      { to: '/instruction-system', label: { de: 'Unterweisungssystem', en: 'Instruction System' } },
      { to: '/produktkatalog?category=arbeitsschutz', label: { de: 'Produktkatalog – Arbeitsschutz & Sicherheit', en: 'Product Catalog – Workplace Safety' } }
    ]
  },
  siemens: {
    id: 'siemens',
    logo: '/assets/partners/siemens.png',
    categories: ['besucherunterweisung', 'unterweisungssystem'],
    website: 'https://www.siemens.com',
    title: {
      de: 'Siemens – Partner für integrierte Zutritts- und Unterweisungslösungen',
      en: 'Siemens – Partner for Integrated Access and Instruction Solutions'
    },
    content: {
      de: {
        intro: 'Siemens zählt weltweit zu den führenden Technologieunternehmen im Bereich Infrastruktur, Gebäudeautomatisierung und Sicherheitssysteme.',
        partnership: 'Im Bereich der digitalen Besucherunterweisung arbeiten Siemens und e-Matrix zusammen, um Zutrittssteuerung und Unterweisung nahtlos miteinander zu verbinden.',
        value: 'Siemens stellt moderne Schließ- und Zutrittssysteme bereit, während e-Matrix mit seinen digitalen Unterweisungslösungen sicherstellt, dass Besucher und Fremdfirmen vor dem Zutritt strukturiert unterwiesen werden und die Schulungen nachvollziehbar dokumentiert sind.',
        impact: 'Durch diese Zusammenarbeit entsteht ein durchgängiger Prozess, bei dem Zutritt und Unterweisung optimal ineinandergreifen und Unternehmen sowohl organisatorisch als auch technisch unterstützt werden.',
        focusTitle: 'Gemeinsame Schwerpunkte',
        focusPoints: [
          'digitale Unterweisung für Mitarbeitende/Fremdfirmen/Besuchende',
          'Zutrittssteuerung und Schließsysteme',
          'Verknüpfung von Unterweisung mit Zutrittsberechtigungen',
          'nachvollziehbare Dokumentation und Rechtssicherheit'
        ],
        visitPartner: 'Besuchen Sie die Seite unseres Partners',
        solutionsTitle: 'Eingesetzte Lösungen und Produkte'
      },
      en: {
        intro: 'Siemens is one of the world\'s leading technology companies in the fields of infrastructure, building automation and security systems.',
        partnership: 'In the area of digital visitor instruction, Siemens and e-Matrix work together to seamlessly connect access control and instruction.',
        value: 'Siemens provides modern locking and access control systems, while e-Matrix uses its digital instruction solutions to ensure that visitors and contractors are systematically instructed before gaining access and that training is documented in a traceable manner.',
        impact: 'This collaboration creates an end-to-end process in which access and instruction are optimally integrated, supporting companies both organizationally and technically.',
        focusTitle: 'Joint Focus Areas',
        focusPoints: [
          'Digital instruction for employees/contractors/visitors',
          'Access control and locking systems',
          'Linking instruction with access permissions',
          'Traceable documentation and legal compliance'
        ],
        visitPartner: 'Visit our partner\'s website',
        solutionsTitle: 'Solutions and Products Used'
      }
    },
    links: [
      { to: '/visitor-system', label: { de: 'Besucherunterweisung', en: 'Visitor Instruction' } },
      { to: '/instruction-system', label: { de: 'Unterweisungssystem', en: 'Instruction System' } }
    ]
  }
};

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' }
};

export default function PartnerDetailPage() {
  const { partnerId } = useParams();
  const { language } = useLanguage();
  
  const partner = partnersData[partnerId];
  
  if (!partner) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#0D2440] mb-4">
            {language === 'de' ? 'Partner nicht gefunden' : 'Partner not found'}
          </h1>
          <Link to="/partner" className="text-[#2E5E99] hover:underline">
            {language === 'de' ? 'Zurück zu Partnern' : 'Back to Partners'}
          </Link>
        </div>
      </div>
    );
  }

  const content = partner.content[language] || partner.content.de;
  const categories = [
    { id: 'unterweisungssystem', label: language === 'de' ? 'Unterweisungssystem' : 'Instruction System' },
    { id: 'besucherunterweisung', label: language === 'de' ? 'Besucherunterweisung' : 'Visitor Instruction' },
    { id: 'arbeitssicherheit', label: language === 'de' ? 'Arbeitssicherheit' : 'Workplace Safety' },
    { id: 'awareness-trainings', label: 'Awareness-Trainings' }
  ];

  const getCategoryLabels = () => {
    return partner.categories
      .map(catId => categories.find(c => c.id === catId)?.label)
      .filter(Boolean)
      .join(', ');
  };

  return (
    <div className="min-h-screen pt-20" data-testid={`partner-detail-${partner.id}`}>
      <SEO
        title={partner.title[language]}
        description={content.intro}
        path={`/partner/${partner.id}`}
        lang={language}
      />
      
      {/* Hero Section with Logo */}
      <section className="py-16 md:py-20 bg-[#E7F0FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/partner" 
            className="inline-flex items-center text-[#2E5E99] hover:text-[#1E4A7A] mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'de' ? 'Zurück zu Partnern' : 'Back to Partners'}
          </Link>
          
          <motion.div {...fadeUp} className="text-center">
            {/* Logo on white background */}
            <div className="bg-white rounded-2xl p-8 md:p-12 mb-8 shadow-sm inline-block mx-auto">
              <img
                src={partner.logo}
                alt={partner.title[language]}
                className="max-h-32 md:max-h-40 w-auto mx-auto object-contain"
              />
            </div>
            
            <span className="text-sm font-semibold text-[#2E5E99] tracking-wider uppercase block mb-4">
              {getCategoryLabels()}
            </span>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0D2440]">
              {partner.title[language]}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="prose prose-lg max-w-none">
            <p className="text-lg text-[#5A7A9B] leading-relaxed mb-6">
              {content.intro}
            </p>
            
            <p className="text-lg text-[#5A7A9B] leading-relaxed mb-6">
              {content.partnership}
            </p>
            
            {content.value && (
              <p className="text-lg text-[#5A7A9B] leading-relaxed mb-6">
                {content.value}
              </p>
            )}
            
            {content.impact && (
              <p className="text-lg text-[#5A7A9B] leading-relaxed mb-6">
                {content.impact}
              </p>
            )}

            {/* Focus Points (for arinco) */}
            {content.focusTitle && (
              <div className="mt-10">
                <h2 className="text-2xl font-bold text-[#0D2440] mb-6">
                  {content.focusTitle}
                </h2>
                <ul className="space-y-3">
                  {content.focusPoints.map((point, idx) => (
                    <li key={`focus-${idx}-${point.slice(0,15)}`} className="flex items-start">
                      <span className="w-2 h-2 bg-[#2E5E99] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span className="text-[#5A7A9B]">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Training Programs (for ACP) */}
            {content.trainingsTitle && (
              <div className="mt-10">
                <h2 className="text-2xl font-bold text-[#0D2440] mb-6">
                  {content.trainingsTitle}
                </h2>
                <div className="space-y-6">
                  {content.trainings.map((training, idx) => (
                    <div key={`training-${training.name.slice(0,15)}`} className="bg-[#F8FAFC] rounded-xl p-6">
                      <h3 className="font-semibold text-[#0D2440] mb-2">
                        {training.name}
                      </h3>
                      <p className="text-[#5A7A9B]">
                        {training.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Links Section */}
      <section className="py-16 md:py-20 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp}>
            {/* Partner Website Link */}
            <div className="mb-10">
              <h3 className="text-lg font-semibold text-[#0D2440] mb-4">
                {content.visitPartner}
              </h3>
              <a
                href={partner.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-[#2E5E99] text-white rounded-xl hover:bg-[#1E4A7A] transition-colors"
              >
                {partner.website.replace('https://', '').replace('www.', '').split('/')[0]}
                <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>

            {/* Solutions Links */}
            <div>
              <h3 className="text-lg font-semibold text-[#0D2440] mb-4">
                {content.solutionsTitle}
              </h3>
              <div className="flex flex-wrap gap-4">
                {partner.links.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="inline-flex items-center px-6 py-3 bg-white border border-[#C8D8E8] text-[#0D2440] rounded-xl hover:bg-[#E7F0FA] transition-colors"
                  >
                    {link.label[language]}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-[#0D2440]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {language === 'de' ? 'Interesse an einer Zusammenarbeit?' : 'Interested in Collaboration?'}
            </h2>
            <p className="text-lg text-white/80 mb-8">
              {language === 'de'
                ? 'Kontaktieren Sie uns und erfahren Sie, wie wir auch Ihr Unternehmen unterstützen können.'
                : 'Contact us and find out how we can support your company too.'}
            </p>
            <Link to="/contact">
              <Button className="bg-[#2E5E99] hover:bg-[#3A6DAA] text-white h-12 px-8 text-base">
                {language === 'de' ? 'Kontakt aufnehmen' : 'Contact Us'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
